<template>
  <div class="profile-page">
    <v-container class="profile-container" fluid>
      <v-row no-gutters class="profile-grid" align="start">
        <v-col cols="12" md="3" class="sidebar-col">
          <div class="sidebar-card">
            <SidebarProfile :current="current" @change="handleChange" />
          </div>
        </v-col>

        <v-col cols="12" md="9" class="content-col">
          <!-- 
          <CommissionPackSection
            v-if="currentUser"
            :current-pack="currentUser.commissionPack || null"
            :wallet-balance="walletBalance"
            class="mb-5"
            @pack-changed="onPackChanged"
          />
          -->

          <div v-if="currentUser" class="mb-5 mx-auto" style="max-width: 1200px;">
            <BanknoteAvatarDialog
              is-static
              show-upgrade
              show-downline-button
              downline-role="user"
              :avatar-url="userAvatarUrl"
              :initials="userInitials"
              :pack-name="currentUser.commissionPack && currentUser.commissionPack.name"
              :user-ref="currentUser.commisionRefCode && currentUser.commisionRefCode.refCode"
              :referrer-ref="currentUser.referrer && currentUser.referrer.refCode"
              :referrer-name="currentUser.referrer && currentUser.referrer.name"
              :referrer-pack-name="currentUser.referrer && currentUser.referrer.packName"
              :alter-text="currentUser.alterText"
              @upgrade="openUpgradeDialog"
              @change="onAvatarClick"
              @save-alter="onSaveAlterText"
            />
          </div>

          <!-- Hidden section just to use its Upgrade Dialog logic -->
          <CommissionPackSection
            v-if="currentUser"
            ref="packSection"
            :current-pack="currentUser.commissionPack || null"
            :wallet-balance="walletBalance"
            style="display:none"
            @pack-changed="onPackChanged"
          />

          <input type="file" ref="avatarInput" @change="handleAvatarChange" style="display:none" accept="image/*" />
          <AvatarCropperDialog :file="cropFile" @done="onCropDone" @cancel="cropFile = null" />

          <div class="content-card">
            <keep-alive>
              <component :is="currentComp" :key="current" :label="currentLabel" :value="currentUser" @save="onSave"
                @delete="onDeleteConfirm" @continue="onDeleteConfirmed" @delete-image="onDeleteImage"
                @avatar-change="onAvatarChange" @save-alter="onSaveAlterText" />
            </keep-alive>
          </div>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import SidebarProfile from "@/components/SidebarProfile.vue";
import CommissionPackSection from "@/components/Shared/CommissionPackSection.vue";
import BanknoteAvatarDialog from "@/components/Shared/BanknoteAvatarDialog.vue";
import AvatarCropperDialog from "@/components/Shared/AvatarCropperDialog.vue";
import FormUpdateProfile from "@/components/ProfileUser/FormUpdateProfile.vue";
import DeleteFormProfile from "@/components/ProfileUser/DeleteFormProfile.vue";
import ManageAddresses from "@/components/ProfileUser/ManageAddresses.vue";
import UpdatePassword from "@/components/ProfileUser/UpdatePassword.vue";
import DeleteProfileContinue from "@/components/ProfileUser/DeleteProfileContinue.vue";
import MyCoins from "@/components/ProfileUser/MyCoins.vue";
import OrderHistory from "@/components/ProfileUser/OrderHistory.vue";
import OrderTracking from "@/components/ProfileUser/OrderTracking.vue";
import Favorites from "@/components/ProfileUser/Favorites.vue";
import Tickets from "@/components/ProfileUser/Tickets.vue";
import TravelHistory from "@/components/ProfileUser/TravelHistory.vue";

import swalAlert from "@/services/alert/swalServices";
import {
  getProfileApi,
  updateProfileApi,
  updateProfileImageApi,
  deleteProfileImageApi,
  updateAlterTextApi,
} from "@/services/api/userServices";
import walletService from "@/services/api/wallet";

const Placeholder = {
  name: "ProfilePlaceholder",
  props: { label: { type: String, default: "" } },
  template: `
    <div>
      <h3 class="ph-title">{{ label }}</h3>
      <p class="ph-sub">กำลังพัฒนา...</p>
    </div>
  `,
};

export default {
  name: "ProfileUser",
  components: {
    SidebarProfile,
    CommissionPackSection,
    BanknoteAvatarDialog,
    AvatarCropperDialog,
    FormUpdateProfile,
    Placeholder,
    DeleteFormProfile,
    ManageAddresses,
    UpdatePassword,
    DeleteProfileContinue,
    MyCoins,
    OrderHistory,
    OrderTracking,
    Favorites,
    Tickets,
    TravelHistory,
  },

  data() {
    return {
      current: "profile_info",
      currentUser: null,
      walletBalance: 0,
      compMap: {
        profile_info: FormUpdateProfile,
        profile_password: UpdatePassword,
        profile_address: ManageAddresses,
        profile_privacy: DeleteFormProfile,
        profile_privacy_continue: DeleteProfileContinue,
        profile_notify: Placeholder,
        orders_tracking: OrderTracking,
        orders_favorites: Favorites,
        wallet_coins: MyCoins,
        orders_history: OrderHistory,
        orders_returns: Placeholder,
        tickets: Tickets,
        travel_history: TravelHistory,
      },
      cropFile: null,
    };
  },

  computed: {
    currentComp() {
      return this.compMap[this.current] || Placeholder;
    },
    currentLabel() {
      return this.menuLabel(this.current);
    },
    userAvatarUrl() {
      if (!this.currentUser || !this.currentUser.profileImage) return "";
      const raw = this.currentUser.profileImage;
      const base = (process.env.VUE_APP_API_URL || "").replace(/\/$/, "");
      return raw.startsWith("http") ? raw : base + (raw.startsWith("/") ? raw : `/${raw}`);
    },
    userInitials() {
      if (!this.currentUser) return "";
      const f = this.currentUser.firstName || "";
      const l = this.currentUser.lastName || "";
      return (f.charAt(0) + l.charAt(0)).toUpperCase();
    },
  },

  watch: {
    "$route.query.tab": {
      handler(newTab) {
        if (newTab && this.compMap[newTab] && newTab !== this.current) {
          this.current = newTab;
        }
      },
      immediate: false,
    },
  },

  methods: {
    handleChange(name) {
      if (!name || name === this.current) return;
      this.current = name;

      // อัพเดต URL query โดยไม่ reload หน้า
      const q = { ...(this.$route.query || {}), tab: name };
      if (this.$route.query.tab !== name) {
        this.$router.replace({ query: q }).catch(() => { });
      }
    },
    openUpgradeDialog() {
      if (this.$refs.packSection) {
        this.$refs.packSection.openDialog();
      }
    },
    onDeleteConfirm() {
      this.current = "profile_privacy_continue";
      const q = { ...(this.$route.query || {}), tab: this.current };
      this.$router.replace({ query: q }).catch(() => { });
    },
    onDeleteConfirmed() {
      this.current = "profile_privacy_continue";
      const q = { ...(this.$route.query || {}), tab: this.current };
      this.$router.replace({ query: q }).catch(() => { });
    },

    async onSaveAlterText(text) {
      try {
        const res = await updateAlterTextApi(text);
        if (res && res.user) {
          this.currentUser = { ...this.currentUser, alterText: res.user.alterText };
        }
      } catch (err) {
        console.error("[SAVE ALTER TEXT ERROR]", err);
      }
    },

    async onAvatarChange(file) {
      try {
        const imgRes = await updateProfileImageApi(file);
        if (imgRes && imgRes.user) {
          this.currentUser = { ...this.currentUser, ...imgRes.user };
          if (this.$store && this.$store.commit) {
            this.$store.commit("auth/SET_USER", this.currentUser);
          }
        }
        swalAlert.Success("เปลี่ยนรูปโปรไฟล์สำเร็จ");
      } catch (err) {
        console.error("[UPDATE AVATAR ERROR]", err);
        swalAlert.Error(err?.response?.data?.message || "อัปโหลดรูปโปรไฟล์ไม่สำเร็จ");
      }
    },

    onAvatarClick() {
      if (this.$refs.avatarInput) this.$refs.avatarInput.click();
    },

    handleAvatarChange(event) {
      const file = event.target.files[0];
      event.target.value = '';
      if (!file) return;
      this.cropFile = file;
    },

    async onCropDone(croppedFile) {
      this.cropFile = null;
      await this.onAvatarChange(croppedFile);
    },

    async onSave(payload) {
      try {
        const profile = payload.profile || payload;

        const res = await swalAlert.Dialog(
          "ยืนยันการแก้ไขข้อมูล",
          "คุณต้องการบันทึกการเปลี่ยนแปลงข้อมูลโปรไฟล์หรือไม่?",
          require("@/assets/alert/save.png"),
          132,
          121,
          "บันทึก",
          "ยกเลิก",
          true
        );

        if (!res.isConfirmed) return;

        const { message, user } = await updateProfileApi(profile);

        this.currentUser = { ...this.currentUser, ...user };

        if (this.$store && this.$store.commit) {
          this.$store.commit("auth/SET_USER", this.currentUser);
        }

        swalAlert.Success(message || "แก้ไขข้อมูลสำเร็จ");
      } catch (err) {
        console.error("[UPDATE PROFILE ERROR]", err);
        const msg =
          err?.response?.data?.message || "อัปเดตข้อมูลผู้ใช้ไม่สำเร็จ";
        swalAlert.Error(msg);
      }
    },

    async onDeleteImage() {
      try {
        const { message, user } = await deleteProfileImageApi();

        if (user) {
          this.currentUser = user;

          if (this.$store && this.$store.commit) {
            this.$store.commit("auth/SET_USER", user);
          }
        } else if (this.currentUser) {
          this.currentUser = {
            ...this.currentUser,
            profileImage: "",
          };
          if (this.$store && this.$store.commit) {
            this.$store.commit("auth/SET_USER", this.currentUser);
          }
        }

        swalAlert.Success(message || "ลบรูปโปรไฟล์สำเร็จ");
      } catch (err) {
        console.error("[DELETE PROFILE IMAGE ERROR]", err);
        const msg = err?.response?.data?.message || "ไม่สามารถลบรูปโปรไฟล์ได้";
        swalAlert.Error(msg);
      }
    },

    async fetchProfile() {
      try {
        const user = await getProfileApi();
        this.currentUser = user;
      } catch (err) {
        console.error("[GET PROFILE ERROR]", err);
      }
    },

    async fetchWalletBalance() {
      try {
        const res = await walletService.getMyWallet("user");
        this.walletBalance = res.data?.topUp?.balance ?? 0;
      } catch {
        this.walletBalance = 0;
      }
    },

    onPackChanged(pack) {
      if (this.currentUser) {
        this.currentUser = { ...this.currentUser, commissionPack: pack, commissionPackId: pack.id };
      }
    },

    menuLabel(key) {
      const dict = {
        profile_info: "ข้อมูลส่วนตัว",
        profile_password: "เปลี่ยนรหัสผ่าน",
        profile_address: "ที่อยู่",
        profile_privacy: "การตั้งค่าความเป็นส่วนตัว",
        profile_privacy_continue: "ยืนยันการลบบัญชี",
        // profile_notify: "ตั้งค่าการแจ้งเตือน",
        orders_tracking: "ติดตามคำสั่งซื้อ",
        orders_favorites: "บันทึกรายการโปรด",
        wallet_coins: "Coins ของฉัน",
        orders_history: "ประวัติการซื้อ",
        orders_returns: "ยกเลิกสินค้า/คืนเงิน",
        tickets: "แจ้งปัญหา / Ticket",
        travel_history: "ประวัติการเรียกรถ",
      };
      return dict[key] || key;
    },
  },

  mounted() {
    const q = this.$route?.query?.tab;
    if (q && this.compMap[q]) this.current = q;

    this.fetchProfile();
    this.fetchWalletBalance();
  },
};
</script>

<style scoped>
.profile-page {
  position: relative;
  min-height: calc(100vh - var(--appbar-h, 64px));
  background-color: rgb(245, 245, 245);
  padding: 20px 0 50px;
}

@media (max-width: 600px) {
  .profile-page {
    padding: 12px 0 40px;
  }
}

.profile-container {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  box-sizing: border-box;
  background-color: transparent;
}

@media (min-width: 1440px) and (max-width: 1919px) {
  .profile-container {
    max-width: 1200px;
  }
}

@media (min-width: 1920px) {
  .profile-container {
    max-width: 1440px;
  }
}

.profile-grid :deep(.v-col) {
  padding-left: 0 !important;
  padding-right: 0 !important;
}

.sidebar-col {
  padding-right: 16px !important;
}

.sidebar-card {
  position: sticky;
  top: calc(var(--appbar-h, 64px) + 16px);
  background: #fff;
  border-radius: 16px;
  padding: 12px;
}

@media (max-width: 960px) {
  .sidebar-col {
    padding-right: 0 !important;
  }

  .sidebar-card {
    position: static;
    top: auto;
    margin-bottom: 12px;
    border-radius: 12px;
    padding: 8px;
  }
}

@media (max-width: 600px) {
  .profile-container {
    padding-left: 16px !important;
    padding-right: 16px !important;
  }

  .sidebar-card {
    padding: 0;
    background: transparent;
    border-radius: 0;
    box-shadow: none;
    margin-bottom: 10px;
  }

  .content-card {
    padding: 0;
  }
}
</style>
